<script setup lang="ts">
import { helpers } from '@vuelidate/validators'

export interface AuthEmailConfirmationFormForm {
  modelValue: boolean | null
  userId: string | null
  email: string | null
  isLogin?: boolean
}

const props = defineProps<AuthEmailConfirmationFormForm>()

const model = useVModel(props, 'modelValue')
const code = ref<string>()
const toast = useToast()

const v$ = useValidation(
  () => ({
    emailConfirmationCode: {
      required: helpers.withMessage(
        'Coloque el código para continuar',
        () => (!!code.value && code.value.length === 6) || !model.value
      ),
    },
  }),
  computed(() => ({ emailConfirmationCode: model.value })),
  {
    $scope: 'auth',
  }
)

const resendEmailVerificationCode = useDebounceFn(
  async () => {
    try {
      await $fetch('/api/__proxy/resendConfirmationEmail', {
        method: 'post',
        body: {
          email: props.email,
        },
        immediate: false,
      })

      toast.add({
        severity: 'success',
        summary: 'Cuenta',
        detail: `Se ha reenviado el código de confirmación a ${props.email}.`,
        life: 10000,
      })
    } catch {
      toast.add({
        severity: 'error',
        summary: 'Cuenta',
        detail: 'No fue posible reenviar el código de confirmación.',
        life: 5000,
      })
    }
  },
  500,
  { maxWait: 60000 }
)

watch(code, async (n) => {
  if (n?.length === 6) {
    try {
      await $fetch('/api/__proxy/confirmEmail', {
        method: 'post',
        body: {
          otpCode: n,
          userId: props.userId,
        },
        immediate: false,
      })

      toast.add({
        severity: 'success',
        summary: 'Cuenta',
        detail: `El correo fue confirmado satisfactoriamente. ${!props.isLogin ? 'Ya puedes acceder iniciando sesión.' : ''}`,
        life: 10000,
      })

      model.value = false
    } catch {
      toast.add({
        severity: 'error',
        summary: 'Cuenta',
        detail: 'No fue posible validar el código de confirmación.',
        life: 5000,
      })
    }
  }
})
</script>

<template>
  <div class="flex flex-col justify-center gap-4 p-5 text-center">
    <div class="text-xl font-bold uppercase">
      {{ !isLogin ? 'Tu cuenta fue creada' : 'Parece que aún no has validado tu cuenta...' }}
    </div>
    <div>
      Te enviamos un e-mail con un código de confirmación a
      <span class="font-bold">{{ email }}</span>
      para validar tu dirección de correo eletrónico.
    </div>
    <InputContainer
      justify="center"
      :error="v$.emailConfirmationCode?.$error"
      :error-message="v$.emailConfirmationCode?.$errors[0]?.$message.toString()"
    >
      <div class="flex justify-center w-full">
        <PvInputOtp
          v-model="code"
          :length="6"
          integer-only
          fluid
        />
      </div>
    </InputContainer>
    <div class="flex flex-col gap-2">
      <PvButton
        outlined
        severity="contrast"
        @click="resendEmailVerificationCode"
      >
        Reenvíame el código de confirmación
      </PvButton>
      <slot name="aditional-actions" />
    </div>
  </div>
</template>
